/**
 * 应用入口文件
 */

import App from './App.vue'
import router from './router'
import pinia from './stores'
import i18n, { initI18n } from './i18n'

// 导入 Element Plus
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
// Element Plus 语言包
import zhCn from 'element-plus/dist/locale/zh-cn.mjs'
import en from 'element-plus/dist/locale/en.mjs'
import ko from 'element-plus/dist/locale/ko.mjs'
import vi from 'element-plus/dist/locale/vi.mjs'

// 导入全局样式
import './assets/styles/index.scss'

// 主题初始化（明/暗，新大厅）
import { initTheme } from './composables/useTheme'

// 导入轮询管理服务
import { loadConfig, startPolling } from './services/pollManager'
// 导入公开接口
import { healthCheck, getLanguages } from './services/api/public'

// 根据当前语言选择 Element Plus 语言包
const getElementPlusLocale = () => {
  const locale = localStorage.getItem('locale') || 'zh'
  const localeMap: Record<string, typeof zhCn> = {
    zh: zhCn,
    'zh-Hans': zhCn,
    en: en,
    ko: ko,
    vi: vi,
  }
  return localeMap[locale] || zhCn
}

// 初始化 vConsole (移动端调试工具)
// const initVConsole = async () => {
//   // 默认启用，通过 URL 参数 ?vconsole=0 可禁用
//   const urlParams = new URLSearchParams(window.location.search)
//   const forceDisable = urlParams.get('vconsole') === '0'

//   if (!forceDisable) {
//     const { default: VConsole } = await import('vconsole')
//     new VConsole()
//   }
// }

/**
 * 加载并缓存语言列表
 */
const loadLanguages = async () => {
  const result = await getLanguages()
  if (result.success && result.data) {
    // 存储到 localStorage 供全局使用
    localStorage.setItem('available_languages', JSON.stringify(result.data))
    if (import.meta.env.DEV) {
      console.log('[App] 可用语言:', result.data)
    }
  }
}

// 最小加载时间 (确保动画至少显示 2 秒)
const minLoadingTime = () => new Promise(resolve => setTimeout(resolve, 2000))

// 先加载配置和 i18n,再创建和挂载应用
Promise.all([
  loadConfig(), // 加载配置文件
  initI18n(), // 初始化 i18n (懒加载当前语言包)
  healthCheck(), // 健康检查
  loadLanguages(), // 加载语言列表
  minLoadingTime(), // 最小加载时间
  // initVConsole(), // 初始化移动端调试工具
]).then(() => {
  // 应用持久化主题（挂载前，减少闪烁）
  initTheme()

  const app = createApp(App)

  // 注册 Element Plus 所有图标
  for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
    app.component(key, component)
  }

  // 使用插件
  app.use(pinia)
  app.use(router)
  app.use(i18n)
  app.use(ElementPlus, {
    locale: getElementPlusLocale(),
  })

  // 挂载应用
  app.mount('#app')

  // 关闭启动加载动画
  const loadingEl = document.getElementById('app-loading')
  if (loadingEl) {
    loadingEl.classList.add('fade-out')
    setTimeout(() => loadingEl.remove(), 300)
  }

  // 启动轮询（配置更新 + 心跳，每 10 秒）
  startPolling(10000)

  // 开发环境日志
  if (import.meta.env.DEV) {
    console.log('[App] 应用已启动')
    console.log('[App] 环境:', import.meta.env.MODE)
    console.log('[App] 当前语言:', i18n.global.locale.value)
    console.log('[App] 配置已加载:', window.APP_CONFIG)
  }
})
